import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, ArrowLeft, Shield, Search } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileCTA from "@/components/MobileCTA";

const NotFoundPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Set noindex to prevent Google from treating this as an alternate page
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (robotsMeta) {
      robotsMeta.setAttribute("content", "noindex, nofollow");
    } else {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      robotsMeta.setAttribute("content", "noindex, nofollow");
      document.head.appendChild(robotsMeta);
    }

    // Remove canonical — a 404 page must NOT declare a canonical to another URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.remove();

    // Set title
    document.title = "Page Not Found | Tucson Stump Grinding";

    return () => {
      // Restore robots to indexable for next page
      const meta = document.querySelector('meta[name="robots"]');
      if (meta) meta.setAttribute("content", "index, follow");

      // Restore canonical element (useSEO on the next page will set the href)
      if (!document.querySelector('link[rel="canonical"]')) {
        const link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        link.setAttribute("href", "https://tucsonstumpgrinding.com/");
        document.head.appendChild(link);
      }

      document.title = "Tucson Stump Grinding | Stump Removal Near Me | Oro Valley | Catalina | Oracle | Saddlebrooke";
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-[#FF4D00] flex items-center justify-center mx-auto mb-6">
            <Search className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-[#1A1A1A] uppercase mb-4" data-testid="404-heading">
            Page Not Found
          </h1>
          <p className="font-body text-lg text-gray-600 mb-8 max-w-lg mx-auto">
            The page <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{location.pathname}</span> doesn't exist. It may have been moved or removed.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 bg-[#FF4D00] hover:bg-[#e64400] text-white px-8 py-4 font-heading font-bold uppercase tracking-wider transition-colors"
              data-testid="404-home-link"
            >
              <ArrowLeft className="w-5 h-5" /> Back to Homepage
            </Link>
            <a
              href="tel:+15202007739,2"
              className="inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-black text-white px-8 py-4 font-heading font-bold uppercase tracking-wider transition-colors"
              data-testid="404-phone-link"
            >
              <Phone className="w-5 h-5" /> Call (520) 200-7739 Ext 2
            </a>
          </div>

          {/* Quick Links */}
          <div className="text-left bg-[#F5F5F0] border-2 border-[#E5E5E0] p-6">
            <h2 className="font-heading text-lg font-bold text-[#1A1A1A] uppercase mb-4">
              Popular Pages
            </h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                { href: "/", label: "Homepage & Free Quote" },
                { href: "/tucson-stump-grinding", label: "Tucson Stump Grinding" },
                { href: "/oro-valley-stump-grinding", label: "Oro Valley Stump Grinding" },
                { href: "/catalina-stump-grinding", label: "Catalina Stump Grinding" },
                { href: "/tree-removal", label: "Tree Removal" },
                { href: "/blog/stump-grinding-cost-tucson", label: "Stump Grinding Cost Guide" },
              ].map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="flex items-center gap-2 font-body text-sm text-gray-700 hover:text-[#FF4D00] py-1 transition-colors"
                >
                  <Shield className="w-3.5 h-3.5 text-[#FF4D00] flex-shrink-0" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <MobileCTA />
    </div>
  );
};

export default NotFoundPage;
